import { memo, useMemo } from "react"
import { useStaticQuery, graphql, Script } from "gatsby"
import { useConfigContext } from "@app/providers/config"
import { useMeta } from "@app/hooks/useMeta"
import { Helmet } from "react-helmet"
import config from "../../config"

import type { Location } from "@root/types/global"

type Props = {
  breadcrumbs?: Array<any>
  data: any
  location: Location
  children?: React.ReactNode
}

// TODO: convert this to use the new Gatsby Head API
// https://www.gatsbyjs.com/blog/introducing-the-gatsby-head-api/
// https://www.gatsbyjs.com/docs/reference/built-in-components/gatsby-head/
// https://www.gatsbyjs.com/docs/reference/release-notes/v4.19/

const Meta: React.FC<Props> = ({ breadcrumbs = [], data, location, children }) => {
  const { getData, getLanguages, getSchemas, getTags, getTracking } = useMeta()
  const {
    settings: { routes },
    store: { gorgiasHelpChatAppId },
  } = useConfigContext()

  const url = location?.pathname
  const isProductRoute = useMemo(() => location?.pathname.includes(routes.PRODUCT), [location?.pathname, routes])
  const languages = getLanguages(url)
  const metadata = getData({
    url,
    data,
    routes,
    breadcrumbs,
    template: data?.template?.metadata,
    language: languages?.find(({ primary }) => primary)?.language?.split("_")?.[0] || "",
  })
  metadata.url = metadata?.siteURL ?? `https://${config?.store?.url}`
  const metatags = getTags(metadata)
  const schemas = getSchemas(metadata)

  const rawProduct = isProductRoute && JSON.parse(data?.product?.shopify?.raw || null)

  const reviewAverage = useMemo(() => {
    return rawProduct?.metafields
      ? rawProduct.metafields.filter(({ key }) => key === "reviews_average")?.map(({ value }) => Math.round(value * 10) / 10)[0]
      : null
  }, [rawProduct])

  const reviewCount = useMemo(() => {
    return rawProduct?.metafields ? rawProduct.metafields?.filter(({ key }) => key === "reviews_count")?.map(({ value }) => value)[0] : null
  }, [rawProduct])

  const reviewScoresMeta = useMemo(() => {
    return rawProduct?.metafields ? rawProduct.metafields?.filter(({ key }) => key === "reviews")?.map(({ value }) => value)[0] : null
  }, [rawProduct])

  const reviewScoresBest = useMemo(() => {
    const bestString = reviewScoresMeta && reviewScoresMeta?.match(/<meta itemprop='bestRating'(?!.*(ignore|someIgnore))[\s\S]*?\/>/g)

    return bestString ? bestString[0]?.substring(bestString[0].indexOf("content='") + 9, bestString[0].lastIndexOf("' />")) : null
  }, [reviewScoresMeta])

  const reviewScoresWorst = useMemo(() => {
    const worstString = reviewScoresMeta && reviewScoresMeta?.match(/<meta itemprop='worstRating'(?!.*(ignore|someIgnore))[\s\S]*?\/>/g)
    return worstString ? worstString[0]?.substring(worstString[0].indexOf("content='") + 9, worstString[0].lastIndexOf("' />")) : null
  }, [reviewScoresMeta])

  const reviewSchema = useMemo(
    () =>
      reviewAverage && {
        aggregateRating: {
          "@type": "AggregateRating",
          ratingCount: `${reviewCount}`,
          ratingValue: `${reviewAverage}`,
          ...(reviewScoresBest && { bestRating: `${reviewScoresBest}` }),
          ...(reviewScoresWorst && { worstRating: `${reviewScoresWorst}` }),
        },
      },
    [reviewAverage, reviewCount, reviewScoresBest, reviewScoresWorst]
  )

  const mergedSchemas = useMemo(() => {
    return schemas?.map(schema => {
      return isProductRoute && schema["@type"] === "Product" && reviewSchema ? { ...schema, ...reviewSchema } : { ...schema }
    })
  }, [isProductRoute, reviewSchema, schemas])

  const tracking = getTracking()

  const { gtm } = useStaticQuery<GatsbyTypes.StaticGTMQuery>(graphql`
    query StaticGTM {
      gtm: sanitySettingTracking {
        googleTagManagerId
      }
    }
  `)

  return (
    <>
      <Helmet htmlAttributes={{ lang: metadata?.metaLang }} />

      {/* eslint-disable */}
      {/* <Helmet htmlAttributes={{ lang: metadata?.metaLang }}> */}
      <title>{metadata?.title}</title>
      <meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=1" />
      <link href={metadata?.siteURL} rel="home" />
      <link href={metadata?.canonical?.replace(/\/$/, "")} rel="canonical" />
      <link href="https://apis.google.com/" rel="dns-prefetch" />
      <link href="https://www.googletagmanager.com/" rel="dns-prefetch" />
      <link href={`https://www.googletagmanager.com/gtm.js?id=${gtm?.googleTagManagerId}`} rel="preload" as="script" />
      {/* <link href={`https://www.googletagmanager.com/gtag/js?id=${gtm?.googleTagManagerId}`} rel="preload" as="script" /> */}

      {metatags?.map(tag => {
        if (tag?.name) {
          return <meta name={tag?.name} key={tag?.name} content={tag?.content} />
        }

        if (tag?.property) {
          return <meta property={tag?.property} key={tag?.property} content={tag?.content} />
        }
      })}

      {languages?.map(({ key, href, hrefLang, rel }) => (
        <link key={key} href={href} hrefLang={hrefLang} rel={rel} />
      ))}

      {mergedSchemas.map((schema, index) => (
        <script type="application/ld+json" key={index}>
          {JSON.stringify(schema)}
        </script>
      ))}

      {/* Elevar */}
      <Script type="text/javascript">
        {`(async () => {
          const settings = {};
          const config = (await import("https://shopify-gtm-suite.getelevar.com/configs/612e0c4b54b59ff4eba4f3f4f7bab89b8f4f9ad9/config.js")).default;
          const scriptUrl = settings.proxyPath
            ? settings.proxyPath + config.script_src_custom_pages_proxied
            : config.script_src_custom_pages;
          if (scriptUrl) {
            const module = await import(scriptUrl);
            await module.handler(config, settings);
          }
        })();`}
      </Script>

      {/* LoyaltyLion. See https://developers.loyaltylion.com/sdk/installation */}
      <Script>
        {`!function(t,n){var e=n.loyaltylion||[];if(!e.isLoyaltyLion){n.loyaltylion=e,void 0===n.lion&&(n.lion=e),e.version=2,e.isLoyaltyLion=!0;var o=n.URLSearchParams,i=n.sessionStorage,r="ll_loader_revision",a=(new Date).toISOString().replace(/-/g,""),s="function"==typeof o?function(){try{var t=new o(n.location.search).get(r);return t&&i.setItem(r,t),i.getItem(r)}catch(t){return""}}():null;c("https://sdk.loyaltylion.net/static/2/"+a.slice(0,8)+"/loader"+(s?"-"+s:"")+".js");var l=!1;e.init=function(t){if(l)throw new Error("Cannot call lion.init more than once");l=!0;var n=e._token=t.token;if(!n)throw new Error("Token must be supplied to lion.init");var o=[];function i(t,n){t[n]=function(){o.push([n,Array.prototype.slice.call(arguments,0)])}}"_push configure bootstrap shutdown on removeListener authenticateCustomer".split(" ").forEach(function(t){i(e,t)}),c("https://sdk.loyaltylion.net/sdk/start/"+a.slice(0,11)+"/"+n+".js"),e._initData=t,e._buffer=o}}function c(n){var e=t.getElementsByTagName("script")[0],o=t.createElement("script");o.src=n,o.crossOrigin="",e.parentNode.insertBefore(o,e)}}(document,window);`}
      </Script>

      <Script defer src="//cdn.shopify.com/shopifycloud/shop-js/client.js"></Script>

      {/* Stamped Reviews */}
      <Script
        async
        type="text/javascript"
        data-api-key="pubkey-CG99P3NwN8SM614FoVXYB2rVB8k3Y1"
        id="stamped-script-widget"
        src="https://cdn1.stamped.io/files/widget.min.js"
      ></Script>

      {/* Okendo Reviews */}
      <meta name="oke:subscriber_id" content="43d8578f-7f97-45eb-ac9b-279168fac58c" />
      <Script async src="https://d3hw6dc1ow8pp2.cloudfront.net/reviews-widget-plus/js/okendo-reviews.js" />

      {/* Gorgias Help Chat Widget */}
      <Script
        id="gorgias-chat-widget-install-v2"
        src={`https://config.gorgias.chat/gorgias-chat-bundle-loader.js?applicationId=${gorgiasHelpChatAppId}`}
        async
        defer
      />

      {/* preconnect to sanity api */}
      <link rel="preconnect" href="https://h6pfw6o3.api.sanity.io/" />

      {/* Stamped reviews widget */}
      {/* See https://support.stamped.io/article/940-showcase-reviews-widgets-outside-of-e-commerce-platform */}
      <Script type="text/javascript" src="https://cdn1.stamped.io/files/widget.min.js" />

      {children}
      {/* </Helmet> */}
      {tracking.map(script => script)}
    </>
  )
}

const MemoMeta = memo(Meta)
export { MemoMeta as Meta }
